import { observable, computed } from 'mobx'
import { orderBy } from 'lodash'
import { isCorrupt, isDeleted, byId } from 'core/utils'
import { MESSAGE_TYPE_CONTACT_INFO } from 'core/constants'
import { freeChatWindowDiscountSecondsLeft } from 'core/freeChatWindow'

class Store {
  env = 'MS-production'
  // authenticated user
  @observable token = null

  // chat view
  @observable conversationUid = null
  @observable.shallow conversations = []
  @observable.shallow users = []
  @observable chat = {
    isOpen: true,
    showScrollbar: null,
  }

  @observable popupType = null;

  @observable user = {
    secondBlock: false,
    block: false,
    premium: false,
    blockMessage: '',
    blockType: null,
    unlockedPrivateImages: CONFIG.unlockedPrivateImages.length ? CONFIG.unlockedPrivateImages : [],
    unlockedVoiceMessages: CONFIG.unlockedVoiceMessages.length ? CONFIG.unlockedVoiceMessages : [],
    sexiconsBlock: false,
  }

  @observable scrollToMessageId = null

  // AVN-1396: when the free chat window closes, on this device's clock: the server reports how long it has left and
  // that is anchored here (see freeChatWindowEnds). The server still decides whether a message is free - `now` only
  // exists so the countdown re-renders.
  @observable freeChatWindowEndsAt = null
  @observable now = Math.floor(Date.now() / 1000)

  // AVN-1396: when side C's discount ends, on the same footing. It outlives the window, so after a reload during those
  // fifteen minutes it is all that says the offer still stands.
  @observable freeChatWindowDiscountEndsAt = null

  // AVN-1396: the side as a letter, so the expiry popup can offer side C the discount dating gives them.
  @observable freeChatWindowTestSide = null

  // AVN-1396: the end of the window whose expiry popup was shown. Lives here rather than on the timer so a remount,
  // or a second header carrying the timer, cannot show it again.
  @observable freeChatWindowExpiryShownFor = null

  @observable giftModalState = false
  @observable actionProfileSearchTerm = ''

  @computed get visibleConversations() {
    return orderBy(this.conversations, ['lastMessageSentAt'], ['desc']).filter(c => !isCorrupt(c) && !isDeleted(c))
  }

  // Get current conversation
  @computed get conversation() {
    const conversations = this.visibleConversations

    const expected = conversations.find(byId(this.conversationUid))
    if (this.conversationUid && !expected && conversations.length) {
      console.warn(`Default conversation not found or was corrupt - ${this.conversationUid}`)
    }
    return expected || conversations[0]
  }

  @computed get anotherActiveConversation() {
    const totalMeMessageCount = this.conversations.reduce((total, c) =>
      total + c.meMessageCount + c.messages.filter(m => m.type === MESSAGE_TYPE_CONTACT_INFO).length, 0)
    return this.conversation.meMessageCount === 0 && totalMeMessageCount > 0
  }

  @computed get unreadMessagesCount() {
    return this.conversations.reduce((total, c) => total + c.unreadByMeCount, 0)
  }

  @computed get freeChatWindowActive() {
    return !!this.freeChatWindowEndsAt && this.freeChatWindowEndsAt > this.now
  }

  @computed get freeChatWindowSecondsLeft() {
    return this.freeChatWindowActive ? this.freeChatWindowEndsAt - this.now : 0
  }

  @computed get freeChatWindowHasDiscount() {
    return this.freeChatWindowTestSide === 'C'
  }

  // AVN-1396 side C: the 20% offer dating sells for fifteen minutes after the window closes. It is offered once the
  // window is over, including after a reload, when only the discount is still running.
  @computed get freeChatWindowDiscountSecondsLeft() {
    return this.freeChatWindowHasDiscount && !this.freeChatWindowActive
      ? freeChatWindowDiscountSecondsLeft(this.freeChatWindowDiscountEndsAt, this.now)
      : 0
  }

  @computed get freeChatWindowDiscountActive() {
    return this.freeChatWindowDiscountSecondsLeft > 0
  }

  @computed get freeChatWindowDiscountExpired() {
    return this.freeChatWindowHasDiscount
      && !this.freeChatWindowActive
      && !!this.freeChatWindowDiscountEndsAt
      && !this.freeChatWindowDiscountActive
  }

  @computed get widgetState() {
    try {
      const widgetState = JSON.parse(localStorage.getItem(`widgetStateFor:${window.CONFIG.userUid}`))
      if (widgetState && typeof widgetState ==='object' && (Date.now() - widgetState.set) < 10800000) {
        this.chat.isOpen = typeof widgetState.isOpen === 'undefined' ? true : widgetState.isOpen
      }
    }
    catch (e) {
      //widgetState was not a JSON stringified object
    }
    return this.chat
  }

  @computed get isDev() {
    return CONFIG.isDev || /(datingfactory|dp-core).com$/.test(location.hostname) || process.env.DEVELOPMENT
  }

  @computed get filteredConversationList() {
    const searchTerm = this.actionProfileSearchTerm.toLowerCase()
    return this.visibleConversations.filter(conv => conv.otherUserProfile.username && conv.otherUserProfile.username.toLowerCase().includes(searchTerm))
  }
}

export default new Store()
